import { Zap, ShieldCheck, Droplets, Globe } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

/* Abstract branded art per pillar — code-built line systems, no images */
const ARTS = {
    speed: (
        <svg viewBox="0 0 220 140" fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="art-spd" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#168BFF" stopOpacity="0" />
                    <stop offset="100%" stopColor="#00D1FF" />
                </linearGradient>
            </defs>
            <path d="M0 50 H150 L128 64 H34" stroke="url(#art-spd)" strokeWidth="2.4" />
            <path d="M0 72 H184 L158 86 H52" stroke="url(#art-spd)" strokeWidth="2.4" opacity="0.55" />
            <path d="M0 94 H116 L100 106 H24" stroke="url(#art-spd)" strokeWidth="2.4" opacity="0.3" />
            <path d="M192 58 l18 14 -18 14" stroke="#00D1FF" strokeWidth="2.4" opacity="0.9" />
            <circle cx="204" cy="72" r="2.4" fill="#00D1FF" />
        </svg>
    ),
    liquidity: (
        <svg viewBox="0 0 220 140" fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="art-liq" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#168BFF" />
                    <stop offset="100%" stopColor="#00D1FF" />
                </linearGradient>
            </defs>
            <path d="M4 70 C 40 34, 78 106, 112 70 S 182 34, 216 70" stroke="url(#art-liq)" strokeWidth="2.2" />
            <path d="M4 90 C 40 54, 78 126, 112 90 S 182 54, 216 90" stroke="url(#art-liq)" strokeWidth="2.2" opacity="0.5" />
            <path d="M4 50 C 40 14, 78 86, 112 50 S 182 14, 216 50" stroke="url(#art-liq)" strokeWidth="2.2" opacity="0.3" />
            <circle cx="216" cy="70" r="3" fill="#00D1FF" />
            <circle cx="216" cy="90" r="2.2" fill="#168BFF" opacity="0.6" />
        </svg>
    ),
    security: (
        <svg viewBox="0 0 220 140" fill="none" aria-hidden="true">
            <path d="M110 12 L170 37 V76 C170 101 141 118 110 128 C79 118 50 101 50 76 V37 Z" stroke="#00D1FF" strokeWidth="1.8" opacity="0.85" />
            <path d="M110 30 L154 48 V76 C154 94 133 106 110 114 C87 106 66 94 66 76 V48 Z" stroke="#168BFF" strokeWidth="1" opacity="0.4" />
            <circle cx="110" cy="70" r="4" fill="#00D1FF" />
            <circle cx="110" cy="36" r="2.4" stroke="#38BDF8" strokeWidth="1.2" />
            <circle cx="150" cy="88" r="2.4" stroke="#38BDF8" strokeWidth="1.2" />
            <circle cx="70" cy="88" r="2.4" stroke="#38BDF8" strokeWidth="1.2" />
            <line x1="110" y1="70" x2="110" y2="36" stroke="#38BDF8" strokeWidth="1" opacity="0.6" />
            <line x1="110" y1="70" x2="150" y2="88" stroke="#38BDF8" strokeWidth="1" opacity="0.6" />
            <line x1="110" y1="70" x2="70" y2="88" stroke="#38BDF8" strokeWidth="1" opacity="0.6" />
        </svg>
    ),
    global: (
        <svg viewBox="0 0 220 140" fill="none" aria-hidden="true">
            <circle cx="110" cy="70" r="36" stroke="#00D1FF" strokeWidth="1.6" opacity="0.8" />
            <ellipse cx="110" cy="70" rx="36" ry="13" stroke="#168BFF" strokeWidth="1" opacity="0.4" />
            <ellipse cx="110" cy="70" rx="13" ry="36" stroke="#168BFF" strokeWidth="1" opacity="0.4" />
            <circle cx="76" cy="52" r="2.6" fill="#00D1FF" />
            <circle cx="144" cy="88" r="2.6" fill="#00D1FF" />
            <circle cx="182" cy="34" r="2.6" stroke="#38BDF8" strokeWidth="1.2" />
            <circle cx="38" cy="106" r="2.6" stroke="#38BDF8" strokeWidth="1.2" />
            <path d="M78 50 Q 130 -6 180 32" stroke="url(#art-glb)" strokeWidth="1.2" opacity="0.7" />
            <path d="M78 54 Q 90 96 42 102" stroke="#38BDF8" strokeWidth="1" opacity="0.5" />
            <defs>
                <linearGradient id="art-glb" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#00D1FF" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                </linearGradient>
            </defs>
        </svg>
    ),
};

const CARDS = [
    {
        n: "01",
        t: "Speed",
        d: "Built around a streamlined trading experience designed for fast market interaction.",
        art: "speed",
        id: "feature-card-speed",
        span: "lg:col-span-7",
    },
    {
        n: "02",
        t: "Security",
        d: "Designed with security-first infrastructure and multiple layers of account protection.",
        art: "security",
        id: "feature-card-security",
        span: "lg:col-span-5",
    },
    {
        n: "03",
        t: "Liquidity",
        d: "Built to provide efficient access to digital asset markets.",
        art: "liquidity",
        id: "feature-card-liquidity",
        span: "lg:col-span-5",
    },
    {
        n: "04",
        t: "Global Access",
        d: "A platform designed for a connected generation of global digital asset users.",
        art: "global",
        id: "feature-card-global",
        span: "lg:col-span-7",
    },
];

export default function WhyTavonx() {
    return (
        <section id="why-tavonx" data-testid="features-section" className="relative bg-base py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow>Why Tavonx</Eyebrow>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="max-w-3xl font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                        Engineered for the <span className="text-grad-blue">next market.</span>
                    </h2>
                </Reveal>

                <div className="mt-14 grid gap-4 lg:grid-cols-12">
                    {CARDS.map((c, i) => (
                        <Reveal key={c.n} delay={i * 0.08} className={c.span}>
                            <div
                                data-testid={c.id}
                                className="group relative h-full overflow-hidden rounded-lg border border-white/[0.07] bg-panel/40 p-8 transition-all duration-500 hover:border-electric/40 lg:p-12"
                            >
                                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(37,99,235,0.14),transparent)] opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                                <span className="pointer-events-none absolute -right-4 -top-6 select-none font-grotesk text-[7rem] font-bold leading-none text-white/[0.03] transition-colors duration-700 group-hover:text-cyber/[0.07] lg:text-[9rem]">
                                    {c.n}
                                </span>

                                {/* branded abstract art */}
                                <div className="pointer-events-none absolute right-6 top-1/2 hidden w-48 -translate-y-1/2 opacity-25 transition-all duration-700 group-hover:opacity-70 group-hover:drop-shadow-[0_0_14px_rgba(0,209,255,0.35)] lg:block">
                                    {ARTS[c.art]}
                                </div>

                                <div className="relative max-w-md">
                                    <div className="flex h-12 w-12 items-center justify-center rounded border border-white/10 bg-white/[0.03] transition-all duration-500 group-hover:border-cyber/50 group-hover:shadow-[0_0_24px_rgba(0,209,255,0.25)]">
                                        {c.t === "Speed" && <Zap className="h-5 w-5 text-cyber" strokeWidth={1.5} />}
                                        {c.t === "Security" && <ShieldCheck className="h-5 w-5 text-cyber" strokeWidth={1.5} />}
                                        {c.t === "Liquidity" && <Droplets className="h-5 w-5 text-cyber" strokeWidth={1.5} />}
                                        {c.t === "Global Access" && <Globe className="h-5 w-5 text-cyber" strokeWidth={1.5} />}
                                    </div>
                                    <h3 className="mt-8 font-grotesk text-2xl font-bold uppercase tracking-wide text-white lg:text-3xl">
                                        {c.t}
                                    </h3>
                                    <p className="mt-4 text-sm leading-relaxed text-slate-400 lg:text-base">
                                        {c.d}
                                    </p>
                                </div>

                                <span className="absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-electric to-cyber transition-transform duration-700 group-hover:scale-x-100" />
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
